"use client";

import { mediaUrl } from "@/lib/media";
import EyeFilled from "@/assets/vectors/EyeFilled";
import Spinner from "@/components/spinner/Spinner";
import Topline from "@/components/topline/Topline";
import CaseStudy, { type CaseStudyMedia } from "@/types/CaseStudy";
import { motion } from 'framer-motion';
import { AnimatePresence } from "motion/react";
import Image from "next/image";
import { useState } from "react";
import styles from "./case_study_gallery.module.scss";
import VideoPlayer from "./video_player/VideoPlayer";

interface CaseStudyGalleryProps {
    caseStudy: CaseStudy;
}

export default function CaseStudyGallery({ caseStudy }: CaseStudyGalleryProps) {
    if (!caseStudy.gallery || caseStudy.gallery.length === 0) {
        return null;
    }

    const items = caseStudy.gallery;

    return (
        <section id="section_gallery" data-project={caseStudy.slug} className={`${styles.container} content-visibility-auto`}>
            <Topline
                icon={EyeFilled}
                label="Visual Showcase"
                tag="h2"
                data-scroll-animation="come-from-bottom"
            />

            <div className={styles.gallery}>
                {items.map((media, index) => (
                    <GalleryMediaCard
                        key={index}
                        media={media}
                        delay={index * 75}
                        imageSizes={caseStudy.slug === "antimanti" ? "(min-width: 900px) 680px, calc(100vw - 48px)" : undefined}
                    />
                ))}
            </div>
        </section>
    );
}

function GalleryMediaCard({
    media,
    delay,
    imageSizes = "(max-width: 720px) 100vw, 1200px",
}: {
    media: CaseStudyMedia;
    delay: number;
    imageSizes?: string;
}) {
    const [loading, setLoading] = useState(true)
    const [failed, setFailed] = useState(false)

    if (media.type === "image") {
        return (
            <div
                className={styles.media_card}
                data-scroll-animation="come-from-bottom"
                data-scroll-animation-delay={delay}
            >
                <div className={styles.media_wrapper}>
                    {failed ? (
                        <div
                            className={styles.media_fallback}
                            role="img"
                            aria-label={media.alt ?? "Preview unavailable"}
                        >
                            <EyeFilled aria-hidden="true" />
                            <span>Preview unavailable</span>
                        </div>
                    ) : (
                        <Image
                            src={mediaUrl(media.src)}
                            alt={media.alt ?? ""}
                            width={media.width ?? 1920}
                            height={media.height ?? 1080}
                            quality={85}
                            sizes={imageSizes}
                            onLoad={() => setLoading(false)}
                            onError={() => {
                                setLoading(false)
                                setFailed(true)
                            }}
                            className={styles.media}
                        />
                    )}

                    <AnimatePresence mode="popLayout">
                        {loading && (
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 0.4,
                                    ease: [0.4, 0, 0.2, 1],
                                    type: 'spring' as const,
                                    stiffness: 200,
                                    damping: 17,
                                }}
                                className={styles.loading}
                            >
                                <Spinner />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                {media.caption && (
                    <p
                        className={styles.caption}
                        dangerouslySetInnerHTML={{ __html: media.caption }}
                    />
                )}
            </div>
        );
    }

    if (media?.type === "video") {
        const {
            width = 1920,
            height = 1080,
            autoplay = false,
            muted = true,
            loop = false,
        } = media;

        return (
            <div
                className={styles.media_card}
                data-scroll-animation="come-from-bottom"
                data-scroll-animation-delay={delay}
            >
                <div className={styles.media_wrapper}>
                    <VideoPlayer
                        src={media.src}
                        autoplay={autoplay}
                        muted={muted}
                        loop={loop}
                        width={width}
                        height={height}
                        onReady={() => setLoading(false)}
                        onError={() => setLoading(false)}
                    />

                    <AnimatePresence mode="popLayout">
                        {loading && (
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 0.4,
                                    ease: [0.4, 0, 0.2, 1],
                                    type: 'spring' as const,
                                    stiffness: 200,
                                    damping: 17,
                                }}
                                className={styles.loading}
                            >
                                <Spinner />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {media.caption && (
                    <p className={styles.caption} dangerouslySetInnerHTML={{ __html: media.caption }} />
                )}
            </div>
        );
    }

    return null;
}
